import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Building2, Loader2, Lock, Trash2, UserPlus, Users } from "lucide-react";
import { httpClient } from "../../HttpClient";
import { useEtatGlobal } from "../EtatGlobal";
import { dialog } from "../../components/ui/Dialog";
import { showStatusToast } from "../../components/ui/Toast";

const ANNUAIRE_API = "https://backend-myalfa.vercel.app/api/gi/annuaire";
const DOSSIERS_API = "https://backend-myalfa.vercel.app/api/clients";

const OFFRES = {
  gratuit: { label: "Gratuit", classe: "bg-slate-100 text-slate-600 border-slate-200" },
  essentiel: { label: "Essentiel", classe: "bg-sky-50 text-sky-700 border-sky-200" },
  pro: { label: "Pro", classe: "bg-[#9B1B5D]/10 text-[#9B1B5D] border-[#9B1B5D]/20" },
  business: { label: "Business", classe: "bg-amber-50 text-amber-700 border-amber-200" },
};

const SOURCES = { offert: "offerte par le cabinet", stripe: "abonnement en ligne", manuel: "posée à la main" };

const nomContact = (c) => `${c.prenoms || ""} ${c.nom || ""}`.trim() || c.email || `Contact ${c.id}`;

// Pastille discrète : le nom de l'offre seul, l'origine (offerte, payée en
// ligne…) en infobulle.
function BadgeOffre({ offre, source }) {
  const o = OFFRES[offre] || OFFRES.gratuit;
  return (
    <span
      title={source && SOURCES[source] ? `Offre ${o.label} · ${SOURCES[source]}` : `Offre ${o.label}`}
      className={`shrink-0 text-[10px] font-semibold leading-4 rounded px-1.5 border ${o.classe}`}
    >
      {o.label}
    </span>
  );
}

function EtatUtilisateur({ u }) {
  if (u.bloque) {
    return (
      <span className="inline-flex shrink-0 items-center gap-1 text-[10px] font-semibold rounded px-1.5 bg-red-50 text-red-700 border border-red-200">
        <Lock className="w-2.5 h-2.5" /> Bloqué
      </span>
    );
  }
  return u.compte_cree ? (
    <span className="shrink-0 text-[10px] font-semibold rounded px-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200">Actif</span>
  ) : (
    <span className="shrink-0 text-[10px] font-semibold rounded px-1.5 bg-orange-50 text-orange-700 border border-orange-200">Invitation envoyée</span>
  );
}

// Compte MyALFA Link d'un contact (fiche Annuaire). Depuis le 30/10/2026,
// chaque SOCIÉTÉ a sa propre offre et ses propres utilisateurs : la carte
// liste les sociétés que ce contact gère, chacune avec son offre, ses places
// et ses utilisateurs. Un utilisateur s'ajoute sur UNE société et ne voit
// qu'elle, dans la limite de son offre (Gratuit/Essentiel 1, Pro 5, Business
// 10, gestionnaires compris). Le cabinet peut toujours ajouter un
// utilisateur : au-delà de la limite, il est bloqué tant que l'offre de la
// société ne change pas. Jonathan et Anthony (`peut_offrir`) changent l'offre
// d'une société depuis la liste. Les règles vivent dans MyALFA Link : ce
// composant ne fait qu'afficher et relayer.
export default function CompteMyalfaLink({ contact }) {
  const { annuaireData } = useEtatGlobal();
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [societeId, setSocieteId] = useState(null);
  const [recherche, setRecherche] = useState("");
  const [choisi, setChoisi] = useState(null);
  const [envoyerInvitation, setEnvoyerInvitation] = useState(true);
  const [occupe, setOccupe] = useState(false);
  const [offreEnCours, setOffreEnCours] = useState(null);

  const charger = useCallback(async () => {
    setErreur(null);
    try {
      const { data: compte } = await httpClient.get(`${ANNUAIRE_API}/${contact.id}/compte-link`);
      setData(compte);
    } catch (err) {
      setErreur(err?.response?.data?.message || "Le compte MyALFA Link n'a pas pu être chargé.");
    }
  }, [contact.id]);

  useEffect(() => {
    setData(null);
    setChoisi(null);
    setRecherche("");
    setSocieteId(null);
    charger();
  }, [charger]);

  const societes = useMemo(() => data?.societes || [], [data]);
  // Société choisie pour l'ajout : la première par défaut.
  const societe = societes.find((s) => s.id === societeId) || societes[0] || null;
  const complete = societe ? societe.places >= societe.limite : false;

  const dejaPresents = useMemo(
    () => new Set([contact.id, ...(societe?.gestionnaires || []).map((g) => g.id), ...(societe?.utilisateurs || []).map((u) => u.id)]),
    [contact.id, societe]
  );

  const suggestions = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    if (q.length < 2) return [];
    return (annuaireData || [])
      .filter((c) => !dejaPresents.has(c.id))
      .filter((c) => `${nomContact(c)} ${c.email || ""}`.toLowerCase().includes(q))
      .slice(0, 6);
  }, [recherche, annuaireData, dejaPresents]);

  const changerOffre = async (s, plan) => {
    const label = OFFRES[plan]?.label;
    const ok = await dialog.confirm(
      plan === "gratuit"
        ? `Repasser ${s.nom} en offre Gratuit ?`
        : `Offrir l'offre ${label} à ${s.nom} ? Le client n'aura rien à payer et ne pourra pas la modifier lui-même.`,
      { title: plan === "gratuit" ? "Repasser en Gratuit" : `Offrir l'offre ${label}`, confirmLabel: "Confirmer" }
    );
    if (!ok) return;
    setOffreEnCours(s.id);
    try {
      await httpClient.put(`${DOSSIERS_API}/${s.id}/offre-link`, { plan });
      showStatusToast({ status: "success", titre: `Offre ${label} appliquée`, message: s.nom });
      await charger();
    } catch (err) {
      showStatusToast({ status: "error", titre: "Offre non modifiée", message: err?.response?.data?.message || "L'offre n'a pas pu être modifiée." });
    } finally {
      setOffreEnCours(null);
    }
  };

  const ajouter = async () => {
    if (!choisi || !societe) return;
    if (complete) {
      const ok = await dialog.confirm(
        `L'offre ${OFFRES[societe.offre]?.label} de ${societe.nom} est complète (${societe.places} / ${societe.limite}). ${nomContact(choisi)} sera ajouté mais bloqué tant que l'offre de cette société ne change pas.`,
        { title: "Offre complète", confirmLabel: "Ajouter quand même" }
      );
      if (!ok) return;
    }
    setOccupe(true);
    try {
      const { data: compteMaj } = await httpClient.post(`${ANNUAIRE_API}/${contact.id}/compte-link/utilisateurs`, {
        memberId: choisi.id,
        clientId: societe.id,
        envoyerInvitation,
      });
      setData(compteMaj);
      setChoisi(null);
      setRecherche("");
      showStatusToast({
        status: "success",
        titre: "Utilisateur ajouté",
        message: `${nomContact(choisi)} a accès à ${societe.nom}${envoyerInvitation ? " ; un e-mail lui a été envoyé" : ""}.`,
      });
    } catch (err) {
      showStatusToast({ status: "error", titre: "Ajout impossible", message: err?.response?.data?.message || "L'utilisateur n'a pas pu être ajouté." });
    } finally {
      setOccupe(false);
    }
  };

  const retirer = async (s, u) => {
    const ok = await dialog.confirm(`Retirer ${u.nom} de ${s.nom} ? Il n'aura plus accès à cette société.`, {
      title: "Retirer un utilisateur",
      confirmLabel: "Retirer",
    });
    if (!ok) return;
    setOccupe(true);
    try {
      const { data: compteMaj } = await httpClient.delete(`${ANNUAIRE_API}/${contact.id}/compte-link/utilisateurs/${u.id}`, {
        params: { clientId: s.id },
      });
      setData(compteMaj);
      showStatusToast({ status: "success", titre: "Utilisateur retiré", message: `${u.nom} n'a plus accès à ${s.nom}.` });
    } catch (err) {
      showStatusToast({ status: "error", titre: "Retrait impossible", message: err?.response?.data?.message || "L'utilisateur n'a pas pu être retiré." });
    } finally {
      setOccupe(false);
    }
  };

  return (
    <div className="bg-white rounded-lg p-4 border border-slate-200">
      <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
        <Users className="w-4 h-4 text-[#9B1B5D]" />
        Compte MyALFA Link
      </h3>

      {erreur ? (
        <p className="text-xs text-red-600">{erreur}</p>
      ) : !data ? (
        <div className="space-y-2 animate-pulse" aria-hidden="true">
          <div className="h-3 w-48 rounded bg-slate-200" />
          <div className="h-14 w-full rounded-md bg-slate-100" />
          <div className="h-14 w-full rounded-md bg-slate-100" />
        </div>
      ) : (
        <div className="space-y-3">
          {societes.length ? (
            <>
              <p className="text-xs text-slate-500">
                <span className="font-semibold text-slate-700">Sociétés gérées par ce contact</span> : chacune a sa propre offre et ses propres utilisateurs.
              </p>

              <div className="flex flex-col gap-2">
                {societes.map((s) => (
                  <div key={s.id} className="rounded-md border border-slate-200">
                    <div className="flex items-center gap-2 bg-slate-50 px-2 py-1.5 rounded-t-md">
                      <Building2 className="w-3.5 h-3.5 shrink-0 text-slate-400" />
                      <span className="min-w-0 flex-1 truncate text-xs font-semibold text-slate-800">{s.nom}</span>
                      <span className="shrink-0 text-[10px] text-slate-500 tabular-nums" title="Places prises / places de l'offre, gestionnaires compris">
                        {s.places} / {s.limite}
                      </span>
                      {data.peut_offrir ? (
                        <select
                          value={s.offre}
                          disabled={offreEnCours === s.id || s.source === "stripe"}
                          title={s.source === "stripe" ? "Payée par carte : l'abonnement doit d'abord être résilié" : "Offrir une offre (hors paiement)"}
                          onChange={(e) => changerOffre(s, e.target.value)}
                          className="shrink-0 rounded border border-slate-200 bg-white px-1 py-0.5 text-[10px] font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-[#9B1B5D] disabled:opacity-60"
                        >
                          {Object.entries(OFFRES).map(([id, o]) => (
                            <option key={id} value={id}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <BadgeOffre offre={s.offre} source={s.source} />
                      )}
                    </div>

                    <div className="px-2 py-1.5 space-y-1">
                      {s.depassement && (
                        <p className="flex items-start gap-1.5 text-[11px] text-amber-800">
                          <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                          L'offre ne permet pas autant d'utilisateurs : les derniers ajoutés sont bloqués.
                        </p>
                      )}
                      {s.utilisateurs.length ? (
                        s.utilisateurs.map((u) => (
                          <div key={u.id} className="flex items-center gap-2">
                            <div className="min-w-0 flex-1">
                              <p className="text-[11px] font-medium text-slate-800 truncate">{u.nom}</p>
                              <p className="text-[10px] text-slate-500 truncate">
                                {u.email || "Sans e-mail"} · ajouté {u.source === "worksync" ? "par le cabinet" : "par le client"}
                              </p>
                            </div>
                            <EtatUtilisateur u={u} />
                            <button
                              type="button"
                              onClick={() => retirer(s, u)}
                              disabled={occupe}
                              className="text-slate-400 hover:text-red-600 disabled:opacity-50"
                              title={`Retirer de ${s.nom}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))
                      ) : (
                        <p className="text-[11px] text-slate-400">Aucun utilisateur invité.</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="pt-2 border-t border-slate-100 space-y-2">
                <p className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                  <UserPlus className="w-3.5 h-3.5" /> Ajouter un utilisateur
                </p>
                <label className="block">
                  <span className="text-[11px] text-slate-500">Société</span>
                  <select
                    value={societe?.id ?? ""}
                    onChange={(e) => {
                      setSocieteId(Number(e.target.value));
                      setChoisi(null);
                    }}
                    className="mt-0.5 w-full rounded-md border border-slate-200 px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-[#9B1B5D]"
                  >
                    {societes.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.nom} — {OFFRES[s.offre]?.label} ({s.places} / {s.limite})
                      </option>
                    ))}
                  </select>
                </label>

                {societe && (
                  <p
                    className={`text-[11px] rounded-md border px-2 py-1.5 ${
                      complete ? "text-amber-800 bg-amber-50 border-amber-200" : "text-slate-600 bg-slate-50 border-slate-200"
                    }`}
                  >
                    {complete
                      ? `L'offre ${OFFRES[societe.offre]?.label} de ${societe.nom} est complète (${societe.places} / ${societe.limite}) : l'utilisateur ajouté sera bloqué tant que cette offre ne change pas.`
                      : `L'utilisateur aura accès à ${societe.nom} uniquement. Places restantes : ${societe.limite - societe.places}.`}
                  </p>
                )}

                {choisi ? (
                  <div className="flex items-center justify-between gap-2 text-xs rounded-md bg-[#9B1B5D]/5 border border-[#9B1B5D]/15 px-2 py-1.5">
                    <span className="truncate">
                      <span className="font-semibold">{nomContact(choisi)}</span>
                      {choisi.email ? ` · ${choisi.email}` : ""}
                    </span>
                    <button type="button" onClick={() => setChoisi(null)} className="text-[11px] text-slate-500 hover:underline">
                      Changer
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <input
                      type="text"
                      value={recherche}
                      onChange={(e) => setRecherche(e.target.value)}
                      placeholder="Rechercher un contact de l'Annuaire (nom ou e-mail)"
                      className="w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-[#9B1B5D]"
                    />
                    {suggestions.length > 0 && (
                      <div className="absolute z-10 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg">
                        {suggestions.map((c) => (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => setChoisi(c)}
                            className="block w-full text-left px-2.5 py-1.5 text-xs hover:bg-slate-50"
                          >
                            <span className="font-medium">{nomContact(c)}</span>
                            <span className="text-slate-400">{c.email ? ` · ${c.email}` : ""}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <p className="text-[11px] text-slate-400">
                  Contact absent ? Créez-le d'abord dans l'Annuaire (« Nouveau contact »), puis ajoutez-le ici.
                </p>
                <label className="flex items-center gap-2 text-[11px] text-slate-600">
                  <input type="checkbox" checked={envoyerInvitation} onChange={(e) => setEnvoyerInvitation(e.target.checked)} />
                  Envoyer l'e-mail d'invitation à l'utilisateur
                </label>
                <button
                  type="button"
                  onClick={ajouter}
                  disabled={!choisi || !societe || occupe}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#840040] px-3 py-2 text-xs font-semibold text-white hover:bg-[#6a0033] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {occupe ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
                  {societe ? `Ajouter à ${societe.nom}` : "Ajouter"}
                </button>
              </div>
            </>
          ) : (
            <p className="text-xs text-slate-500">
              Ce contact ne gère aucune société : il n'a pas de compte MyALFA Link à lui. Rattachez-lui une société pour qu'il en ait un.
            </p>
          )}

          {(data.membre_de || []).length > 0 && (
            <div className="pt-2 border-t border-slate-100 space-y-1.5">
              <p className="text-xs font-semibold text-slate-700">Utilisateur invité sur</p>
              {data.membre_de.map((m) => (
                <div key={m.id} className="flex items-center gap-2 rounded-md border border-slate-200 px-2 py-1.5">
                  <Building2 className="w-3 h-3 shrink-0 text-slate-400" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-medium text-slate-800 truncate">{m.nom}</p>
                    {m.invite_par && <p className="text-[10px] text-slate-500 truncate">Invité par {m.invite_par.nom}</p>}
                  </div>
                  <BadgeOffre offre={m.offre} />
                  {m.bloque && (
                    <span
                      className="inline-flex shrink-0 items-center gap-1 text-[10px] font-semibold rounded px-1.5 bg-red-50 text-red-700 border border-red-200"
                      title="L'offre de cette société ne permet pas autant d'utilisateurs"
                    >
                      <Lock className="w-2.5 h-2.5" /> Bloqué
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
